<template>
  <div
    @dragenter.prevent="onExternalDragEnter"
    @dragover.prevent="onExternalDragOver"
    @dragleave.prevent="onExternalDragLeave"
    @drop="externalDropHandler($event)"
    :class="{ dragging: hasExternalDrag }"
    class="repo-container"
  >
    <div v-if="hasExternalDrag" class="drag-overlay">
      <div class="drag-content">
        <i class="icon-upload" style="font-size: 48px; color: #046af3" />

        <p>Solte os arquivos aqui para fazer upload</p>

        <p style="font-size: 12px; color: #7d8797; max-width: 360px; text-align: center">
          {{ mediaLimitsHint }}
        </p>
      </div>
    </div>

    <div class="filter-holder">
      <div class="line-holder">
        <div v-if="currentPath !== '/'" @click="handlePathReturn()">
          <Icon icon="material-symbols:arrow-back-ios-new" class="back-icon" />
        </div>

        <div class="path">{{ currentPath }}</div>
      </div>

      <div class="actions-holder">
        <search-input-field
          background-color="#ffffff"
          color="#2f343d"
          input-height="40px"
          class="search-input"
          placeholder="Pesquisar arquivos..."
          v-model="nameFilter"
        />

        <div v-if="canToggleViewMode" class="view-mode-toggle">
          <div @click="setViewMode('grid')" :class="{ active: viewMode === 'grid' }" class="toggle-option">
            <Icon icon="material-symbols:grid-view" class="toggle-icon" />
          </div>

          <div @click="setViewMode('list')" :class="{ active: viewMode === 'list' }" class="toggle-option">
            <Icon icon="material-symbols:format-list-bulleted" class="toggle-icon" />
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="viewMode === 'grid'"
      @contextmenu.prevent.stop="handleRootContext($event)"
      @scroll="closePreview()"
      class="files-box nice-Vscroll"
    >
      <div v-if="!listFiles.length" class="empty-repo">{{ emptyStateText }}</div>

      <div
        v-for="(file, index) in currentFiles"
        @click="handleFileSelect(file)"
        @dragstart="onInternalDragStart($event, file)"
        @dragend="onInternalDragEnd()"
        @dragenter="onInternalDragEnter($event, file)"
        @dragover="onInternalDragOver($event, file)"
        @dragleave="onInternalDragLeave($event, file)"
        @drop="onInternalDrop($event, file)"
        @contextmenu.prevent="openContextMenu($event, file)"
        @mouseenter="handlePreviewEnter($event, file)"
        @mouseleave="handlePreviewLeave()"
        :key="index"
        :class="{ 'drop-target': dragOverId === file.id }"
        class="file-item"
        draggable="true"
      >
        <div v-if="file.children && Object.keys(file.children).length" class="file-counter">
          <span>{{ Object.keys(file.children).length }}</span>
        </div>

        <Icon :icon="getIconByFile(file)" :color="getColorByFile(file)" class="file-icon" />

        <div v-if="nameEditingId != file.name" :id="getFileElementId(file)" class="file-name">
          {{ file.name }}

          <b-tooltip :target="getFileElementId(file)" placement="bottom">
            {{ file.name }}
          </b-tooltip>
        </div>

        <div
          v-else
          @click.stop
          v-click-outside="
            () => {
              handleConfirmNameEdit(file);
            }
          "
          class="file-name-input-holder"
        >
          <input id="file-name-input" type="text" v-model="editingName" />

          <div @click="handleConfirmNameEdit(file)">
            <Icon icon="material-symbols:check" class="name-editing-icon" />
          </div>
        </div>
      </div>
    </div>

    <div
      v-else
      @contextmenu.prevent.stop="handleRootContext($event)"
      @scroll="closePreview()"
      class="repo-list nice-Vscroll"
    >
      <div class="list-header">
        <div class="header-name">Nome</div>

        <div v-if="searchMode" class="header-path">Local</div>

        <div class="header-date">Criado em</div>
      </div>

      <div v-if="!listFiles.length" class="empty-repo">{{ emptyStateText }}</div>

      <div
        v-for="(file, index) in listFiles"
        @click="handleListClick(file)"
        @dragstart="onInternalDragStart($event, file)"
        @dragend="onInternalDragEnd()"
        @dragenter="onInternalDragEnter($event, file)"
        @dragover="onInternalDragOver($event, file)"
        @dragleave="onInternalDragLeave($event, file)"
        @drop="onInternalDrop($event, file)"
        @contextmenu.prevent="openContextMenu($event, file)"
        @mouseenter="handlePreviewEnter($event, file)"
        @mouseleave="handlePreviewLeave()"
        :key="index"
        :class="{ 'drop-target': dragOverId === file.id }"
        class="repo-row"
        draggable="true"
      >
        <Icon :icon="getIconByFile(file)" :color="getColorByFile(file)" class="row-icon" />

        <div v-if="nameEditingId != file.name" class="row-name">
          <span class="row-name-text">{{ file.name }}</span>

          <span v-if="getChildrenCount(file)" class="row-count">({{ getChildrenCount(file) }})</span>
        </div>

        <div
          v-else
          @click.stop
          v-click-outside="
            () => {
              handleConfirmNameEdit(file);
            }
          "
          class="row-name-input-holder"
        >
          <input id="file-name-input" type="text" v-model="editingName" />

          <div @click="handleConfirmNameEdit(file)">
            <Icon icon="material-symbols:check" class="name-editing-icon" />
          </div>
        </div>

        <div v-if="searchMode" class="row-path">{{ file.relative_path }}</div>

        <div class="row-date">{{ getFileDate(file) }}</div>
      </div>
    </div>

    <div
      v-if="contextMenu.visible && contextMenu.file"
      v-click-outside="
        () => {
          closeContextMenu();
        }
      "
      class="repo-context-menu"
      :style="{ top: contextMenu.y + 'px', left: contextMenu.x + 'px' }"
    >
      <ul>
        <li @click="(handleFileSelect(contextMenu.file), closeContextMenu())">
          {{ editMode ? "Abrir" : "Selecionar" }}
        </li>

        <li v-if="editMode" @click="(handleFileRename(contextMenu.file), closeContextMenu())">Renomear</li>

        <li v-if="editMode" @click="(deleteFile(contextMenu.file.id), closeContextMenu())">Apagar</li>

        <li
          v-if="contextMenu.file.url && editMode"
          @click="(handleFileDownload(contextMenu.file), closeContextMenu())"
        >
          Download
        </li>
      </ul>
    </div>

    <div
      v-if="contextMenu.visible && !contextMenu.file"
      v-click-outside="
        () => {
          closeContextMenu();
        }
      "
      class="repo-context-menu"
      :style="{ top: contextMenu.y + 'px', left: contextMenu.x + 'px' }"
    >
      <ul>
        <li v-if="editMode" @click="(handleCreateFolder(), closeContextMenu())">Criar pasta</li>

        <li v-if="editMode" @click="(handleUploadFiles(), closeContextMenu())">Upload de arquivos</li>

        <li @click="($emit('local-refresh'), closeContextMenu())">Atualizar</li>

        <li v-if="selectedPath" @click="(handlePathReturn(), closeContextMenu())">Voltar</li>
      </ul>
    </div>

    <div
      v-if="preview.visible && preview.file"
      @mouseenter="clearPreviewCloseTimer()"
      @mouseleave="handlePreviewBoxLeave($event)"
      :style="{ top: preview.y + 'px', left: preview.x + 'px' }"
      class="repo-preview"
    >
      <div class="preview-content-holder">
        <b-spinner v-if="hasPreviewLoad" small type="grow" variant="primary" />

        <img
          v-if="previewKind === 'image'"
          v-show="!hasPreviewLoad"
          @load="hasPreviewLoad = false"
          @error="closePreview()"
          :src="previewSrc"
          class="preview-media"
          alt=""
        />

        <video
          v-else-if="previewKind === 'video'"
          v-show="!hasPreviewLoad"
          @loadedmetadata="hasPreviewLoad = false"
          @error="closePreview()"
          :src="previewSrc"
          class="preview-media"
          preload="metadata"
          muted
        />

        <iframe
          v-else-if="previewKind === 'pdf'"
          v-show="!hasPreviewLoad"
          @load="hasPreviewLoad = false"
          :src="previewSrc"
          class="preview-frame"
        />

        <pre v-else-if="previewKind === 'text'" v-show="!hasPreviewLoad" class="preview-text">{{
          previewText
        }}</pre>
      </div>

      <div class="preview-name">{{ preview.file.name }}</div>
    </div>

    <div v-if="hasSaveRequest" class="absolute-loading">
      <b-spinner small type="grow" variant="primary" />
    </div>
  </div>
</template>

<script>
import { BSpinner, BTooltip } from "bootstrap-vue";
import { Icon } from "@iconify/vue2";
import vClickOutside from "v-click-outside";

import SearchInputField from "@/views/components/SearchInputField.vue";
import {
  partitionMediaFiles,
  MEDIA_ACCEPT_ATTRIBUTE,
  getMediaLimitsSummary,
} from "@/services/mediaValidationService";

const PREVIEW_WIDTH = 280;
const PREVIEW_HEIGHT = 280;
const PREVIEW_DELAY = 350;
const PREVIEW_CLOSE_DELAY = 200;
const PREVIEW_TEXT_LIMIT = 2000;

export default {
  directives: {
    clickOutside: vClickOutside.directive,
  },

  components: {
    BSpinner,
    BTooltip,
    Icon,

    SearchInputField,
  },

  props: {
    data: {
      required: true,
      default: null,
    },

    searchData: {
      required: false,
      default: null,
    },

    totalCount: {
      type: Number,
      required: true,
      default: null,
    },

    searchMode: {
      type: Boolean,
      required: false,
      default: false,
    },

    selectMode: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  data() {
    return {
      filterTimer: null,
      nameFilter: this.checkForFilters("search"),

      hasExternalDrag: false,
      hasSaveRequest: false,

      internalDragActive: false,
      draggedItem: null,
      externalDragCounter: 0,
      dragOverId: null,

      editingName: "",
      nameEditingId: null,

      previewTimer: null,
      previewCloseTimer: null,
      hasPreviewLoad: false,
      previewText: "",
      previewRequestId: 0,

      contextMenu: {
        visible: false,
        x: 0,
        y: 0,
        file: null,
      },

      preview: {
        visible: false,
        x: 0,
        y: 0,
        file: null,
      },

      possibleIcons: {
        // Documentos de texto
        "text/plain": "icon-park-twotone:file-text",
        "application/pdf": "icon-park-twotone:file-pdf",
        "application/msword": "icon-park-twotone:file-word",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
          "icon-park-twotone:file-word",
        "application/vnd.ms-excel": "icon-park-twotone:excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "icon-park-twotone:excel",
        "application/vnd.ms-powerpoint": "icon-park-twotone:ppt",
        "application/vnd.openxmlformats-officedocument.presentationml.presentation": "icon-park-twotone:ppt",
        "application/rtf": "icon-park-twotone:file-text",

        // Tabelas
        "text/csv": "icon-park-twotone:file-excel",

        // Imagens
        "image/png": "icon-park-twotone:image-files",
        "image/jpeg": "icon-park-twotone:image-files",
        "image/jpg": "icon-park-twotone:image-files",
        "image/gif": "icon-park-twotone:image-files",
        "image/bmp": "icon-park-twotone:image-files",
        "image/webp": "icon-park-twotone:image-files",
        "image/svg+xml": "icon-park-twotone:image-files",
        "image/tiff": "icon-park-twotone:image-files",
        "image/x-icon": "icon-park-twotone:image-files",

        // Vídeos
        "video/mp4": "icon-park-twotone:video",
        "video/mpeg": "icon-park-twotone:video",
        "video/quicktime": "icon-park-twotone:video",
        "video/x-msvideo": "icon-park-twotone:video",
        "video/x-flv": "icon-park-twotone:video",
        "video/webm": "icon-park-twotone:video",
        "video/x-matroska": "icon-park-twotone:video",

        // Áudio
        "audio/mpeg": "icon-park-twotone:music",
        "audio/mp3": "icon-park-twotone:music",
        "audio/wav": "icon-park-twotone:music",
        "audio/ogg": "icon-park-twotone:music",
        "audio/webm": "icon-park-twotone:music",
        "audio/aac": "icon-park-twotone:music",
        "audio/flac": "icon-park-twotone:music",

        // Arquivos compactados
        "application/zip": "icon-park-twotone:zip",
        "application/x-rar-compressed": "icon-park-twotone:zip",
        "application/x-7z-compressed": "icon-park-twotone:zip",
        "application/x-tar": "icon-park-twotone:zip",
        "application/gzip": "icon-park-twotone:zip",

        // Código
        "text/html": "icon-park-twotone:file-code",
        "text/css": "icon-park-twotone:file-code",
        "text/javascript": "icon-park-twotone:file-code",
        "application/javascript": "icon-park-twotone:file-code",
        "application/json": "icon-park-twotone:file-code",
        "application/xml": "icon-park-twotone:file-code",
        "text/xml": "icon-park-twotone:file-code",
        "text/x-python": "icon-park-twotone:file-code",
        "text/x-java": "icon-park-twotone:file-code",
        "text/x-c": "icon-park-twotone:file-code",
        "text/x-php": "icon-park-twotone:file-code",

        // Outros
        "application/octet-stream": "icon-park-twotone:file-question",
      },

      possibleIconsColors: {
        // Documentos de texto
        "text/plain": "#4A90E2",
        "application/pdf": "#E64A19",
        "application/msword": "#2B579A",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "#2B579A",
        "application/vnd.ms-excel": "#217346",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "#217346",
        "application/vnd.ms-powerpoint": "#D24726",
        "application/vnd.openxmlformats-officedocument.presentationml.presentation": "#D24726",
        "application/rtf": "#4A90E2",

        // Tabelas
        "text/csv": "#217346",

        // Imagens
        "image/png": "#FFCA28",
        "image/jpeg": "#FFCA28",
        "image/jpg": "#FFCA28",
        "image/gif": "#FFCA28",
        "image/bmp": "#FFCA28",
        "image/webp": "#FFCA28",
        "image/svg+xml": "#FFCA28",
        "image/tiff": "#FFCA28",
        "image/x-icon": "#FFCA28",

        // Vídeos
        "video/mp4": "#9C27B0",
        "video/mpeg": "#9C27B0",
        "video/quicktime": "#9C27B0",
        "video/x-msvideo": "#9C27B0",
        "video/x-flv": "#9C27B0",
        "video/webm": "#9C27B0",
        "video/x-matroska": "#9C27B0",

        // Áudio
        "audio/mpeg": "#FF5722",
        "audio/mp3": "#FF5722",
        "audio/wav": "#FF5722",
        "audio/ogg": "#FF5722",
        "audio/webm": "#FF5722",
        "audio/aac": "#FF5722",
        "audio/flac": "#FF5722",

        // Arquivos compactados
        "application/zip": "#795548",
        "application/x-rar-compressed": "#795548",
        "application/x-7z-compressed": "#795548",
        "application/x-tar": "#795548",
        "application/gzip": "#795548",

        // Código
        "text/html": "#00BCD4",
        "text/css": "#00BCD4",
        "text/javascript": "#00BCD4",
        "application/javascript": "#00BCD4",
        "application/json": "#00BCD4",
        "application/xml": "#00BCD4",
        "text/xml": "#00BCD4",
        "text/x-python": "#00BCD4",
        "text/x-java": "#00BCD4",
        "text/x-c": "#00BCD4",
        "text/x-php": "#00BCD4",

        // Outros
        "application/octet-stream": "#607D8B",
      },
    };
  },

  watch: {
    nameFilter(oldValue, newValue) {
      if (oldValue != newValue) {
        this.emitFilter();
      }
    },
  },

  computed: {
    mediaAccept() {
      return MEDIA_ACCEPT_ATTRIBUTE;
    },

    mediaLimitsHint() {
      return getMediaLimitsSummary();
    },

    editMode() {
      return !this.selectMode && !this.searchMode;
    },

    viewMode() {
      if (this.selectMode || this.searchMode) return "list";

      return this.$store.getters["admin/getRepoViewMode"];
    },

    canToggleViewMode() {
      return !this.selectMode && !this.searchMode;
    },

    selectedPath() {
      return this.$store.getters["admin/getRepoSelectedPath"];
    },

    currentPath() {
      return this.searchMode ? "/" : this.$store.getters["admin/getRepoSelectedPath"] || "/";
    },

    currentFiles() {
      const selectedPath = this.$store.getters["admin/getRepoSelectedPath"];

      if (selectedPath) {
        const pathParts = selectedPath.split("/").filter((part) => part);

        let currentLevel = this.data;

        for (const part of pathParts) {
          if (currentLevel[part] && currentLevel[part].children) {
            currentLevel = currentLevel[part].children;
          } else {
            return {};
          }
        }

        return currentLevel;
      }

      return this.data;
    },

    searchResults() {
      if (!this.searchMode) return [];

      const list = Array.isArray(this.searchData) ? this.searchData : Object.values(this.searchData || {});

      return list.filter((f) => !!f && !!f.name && !!f.relative_path);
    },

    listFiles() {
      if (this.searchMode) return this.searchResults;

      return Object.values(this.currentFiles || {});
    },

    emptyStateText() {
      return this.searchMode ? "Nenhum arquivo encontrado." : "Nenhum arquivo nesta pasta.";
    },

    previewKind() {
      return this.preview.file ? this.getPreviewKind(this.preview.file) : null;
    },

    previewSrc() {
      if (!this.preview.file) return "";

      const url = this.preview.file.url;

      if (this.previewKind === "pdf") return `${url}#toolbar=0&navpanes=0&view=FitH`;
      if (this.previewKind === "video") return `${url}#t=0.1`;

      return url;
    },
  },

  mounted() {
    this.$eventBus.on("open-file-selection", () => {
      this.handleUploadFiles();
    });
  },

  beforeDestroy() {
    this.$eventBus.off("open-file-selection");

    this.clearPreviewTimer();
  },

  methods: {
    setFilters() {
      const queries = [{ query: "search", param: this.nameFilter }];

      this.$store.dispatch("admin/setRepoFilters", queries);
    },

    setViewMode(mode) {
      if (mode === this.viewMode) return;

      this.closePreview();

      this.$store.dispatch("admin/setRepoViewMode", mode);
    },

    emitFilter() {
      if (this.filterTimer) {
        clearTimeout(this.filterTimer);
      }

      this.filterTimer = setTimeout(() => {
        this.setFilters();

        this.$emit("filterTable");
      }, 1000);
    },

    clearSearch() {
      this.nameFilter = "";

      this.setFilters();

      this.$emit("filterTable");
    },

    checkForFilters(query) {
      const filters = this.$store.getters["admin/getRepoFilters"];

      if (filters?.length) {
        const nameFilter = filters.filter((filter) => {
          return filter.query == query;
        });

        return nameFilter[0]?.param;
      }
    },

    closeContextMenu() {
      this.contextMenu.visible = false;
      this.contextMenu.file = null;
    },

    clearNameEdit() {
      this.nameEditingId = null;
      this.editingName = "";
    },

    clearPreviewTimer() {
      if (this.previewTimer) {
        clearTimeout(this.previewTimer);

        this.previewTimer = null;
      }
    },

    clearPreviewCloseTimer() {
      if (this.previewCloseTimer) {
        clearTimeout(this.previewCloseTimer);

        this.previewCloseTimer = null;
      }
    },

    schedulePreviewClose() {
      this.clearPreviewCloseTimer();

      this.previewCloseTimer = setTimeout(() => {
        this.closePreview();
      }, PREVIEW_CLOSE_DELAY);
    },

    closePreview() {
      this.clearPreviewTimer();
      this.clearPreviewCloseTimer();

      this.previewRequestId++;

      this.preview.visible = false;
      this.preview.file = null;
      this.hasPreviewLoad = false;
      this.previewText = "";
    },

    getIconByFile(file) {
      const isFolder = (!file.mime && !file.url) || (file.children && Object.keys(file.children).length);

      if (isFolder) return "material-symbols:folder-open";

      return this.possibleIcons[file.mime] || "icon-park-twotone:file-question";
    },

    getColorByFile(file) {
      const isFolder = (!file.mime && !file.url) || (file.children && Object.keys(file.children).length);

      if (isFolder) return "#FFB300";

      return this.possibleIconsColors[file.mime] || "#607D8B";
    },

    getNameWithoutExtension(fullName) {
      const nameParts = fullName.split(".");

      return nameParts.slice(0, -1).join(".");
    },

    getFileName(fullName) {
      let hasNameConflict = false;

      Object.entries(this.currentFiles).forEach(([key]) => {
        if (key === fullName) hasNameConflict = true;
      });

      if (hasNameConflict) {
        const nameWithoutType = this.getNameWithoutExtension(fullName);
        const type = fullName.split(".").pop();

        let counter = 1;
        let newName = `${nameWithoutType}(${counter}).${type}`;

        while (hasNameConflict) {
          hasNameConflict = false;

          Object.entries(this.currentFiles).forEach(([key]) => {
            if (key === newName) hasNameConflict = true;
          });

          if (hasNameConflict) {
            counter += 1;
            newName = `${nameWithoutType}(${counter}).${type}`;
          }
        }

        return newName;
      }

      return fullName;
    },

    getFolderName(inputName) {
      let folderName = inputName.trim();

      let hasNameConflict = false;

      Object.entries(this.currentFiles).forEach(([key]) => {
        if (key === folderName) hasNameConflict = true;
      });

      if (hasNameConflict) {
        let counter = 1;
        let newFolderName = `${folderName}(${counter})`;

        while (hasNameConflict) {
          hasNameConflict = false;

          Object.entries(this.currentFiles).forEach(([key]) => {
            if (key === newFolderName) hasNameConflict = true;
          });

          if (hasNameConflict) {
            counter += 1;
            newFolderName = `${folderName}(${counter})`;
          }
        }

        return newFolderName;
      }

      return folderName;
    },

    getPreviewKind(file) {
      if (!file.url || !file.mime) return null;

      if (file.mime.startsWith("image/")) return "image";
      if (file.mime.startsWith("video/")) return "video";
      if (file.mime.startsWith("text/")) return "text";
      if (file.mime === "application/pdf") return "pdf";

      return null;
    },

    getFileElementId(file) {
      return `repo-file-${file.id}`;
    },

    getChildrenCount(file) {
      return file.children ? Object.keys(file.children).length : 0;
    },

    getFileDate(file) {
      if (!file.created_at) return "";

      return this.$service.dateFormat(file.created_at, "YYYY-MM-DD HH:mm:ss", "DD/MM/YYYY");
    },

    getPreviewPosition(targetRect) {
      const containerRect = this.$el.getBoundingClientRect();

      const maxX = containerRect.width - PREVIEW_WIDTH - 4;
      const maxY = containerRect.height - PREVIEW_HEIGHT - 4;

      const anchorX = targetRect.left - containerRect.left + 24;
      const belowY = targetRect.bottom - containerRect.top + 8;

      const anchorY = belowY > maxY ? targetRect.top - containerRect.top - PREVIEW_HEIGHT - 8 : belowY;

      return {
        x: Math.max(4, Math.min(anchorX, maxX)),
        y: Math.max(4, Math.min(anchorY, maxY)),
      };
    },

    isExternalFileDrag(event) {
      return !!(
        event &&
        event.dataTransfer &&
        event.dataTransfer.types &&
        Array.from(event.dataTransfer.types).includes("Files")
      );
    },

    onExternalDragEnter(event) {
      if (!this.editMode) return;

      if (this.internalDragActive) return;
      if (!this.isExternalFileDrag(event)) return;

      this.externalDragCounter++;
      this.hasExternalDrag = true;
    },

    onExternalDragOver(event) {
      if (this.internalDragActive) return;
      if (!this.isExternalFileDrag(event)) return;

      this.hasExternalDrag = true;
    },

    onExternalDragLeave(event) {
      if (this.internalDragActive) return;
      if (!this.isExternalFileDrag(event)) return;

      this.externalDragCounter = Math.max(0, this.externalDragCounter - 1);

      if (this.externalDragCounter === 0) this.hasExternalDrag = false;
    },

    async externalDropHandler(event) {
      if (this.internalDragActive) return;
      if (!this.isExternalFileDrag(event)) return;

      event.preventDefault();

      const files = event.dataTransfer.files;

      this.externalDragCounter = 0;
      this.hasExternalDrag = false;

      if (files.length > 0) await this.uploadFilesSequentially(files);
    },

    onInternalDragStart(event, file) {
      if (!this.editMode) return;

      this.closePreview();

      this.internalDragActive = true;

      this.hasExternalDrag = false;
      this.draggedItem = file;

      event.dataTransfer.effectAllowed = "move";
      event.dataTransfer.setData("text/plain", JSON.stringify({ id: file.id, name: file.name }));
    },

    onInternalDragEnd() {
      this.internalDragActive = false;
      this.draggedItem = null;
      this.dragOverId = null;
    },

    onInternalDragOver(event, file) {
      if (!this.editMode) return;

      if (!this.internalDragActive) {
        if (this.isExternalFileDrag(event)) this.hasExternalDrag = true;

        return;
      }

      const isFolder = (!file.mime && !file.url) || (file.children && Object.keys(file.children).length);

      if (isFolder) {
        event.preventDefault();
        event.stopPropagation();

        event.dataTransfer.dropEffect = "move";

        if (this.draggedItem && this.draggedItem.id !== file.id) this.dragOverId = file.id;
      }
    },

    onInternalDragLeave(event, file) {
      if (!this.editMode) return;

      if (!this.internalDragActive) {
        if (this.isExternalFileDrag(event)) {
          this.externalDragCounter = Math.max(0, this.externalDragCounter - 1);

          if (this.externalDragCounter === 0) this.hasExternalDrag = false;
        }

        return;
      }

      if (file && this.dragOverId === file.id) this.dragOverId = null;
    },

    onInternalDragEnter(event) {
      if (!this.editMode) return;
      if (this.internalDragActive) return;

      if (this.isExternalFileDrag(event)) {
        this.externalDragCounter++;
        this.hasExternalDrag = true;
      }
    },

    onInternalDrop(event, targetFile) {
      if (!this.internalDragActive || !this.draggedItem) return;

      const targetIsFolder =
        (!targetFile.mime && !targetFile.url) ||
        (targetFile.children && Object.keys(targetFile.children).length);

      if (!targetIsFolder) return;

      event.preventDefault();
      event.stopPropagation();

      if (this.draggedItem && targetFile && this.draggedItem.id === targetFile.id) return;

      const newPath = `${targetFile.relative_path}/${this.draggedItem.name}`;
      const isFolder =
        (!this.draggedItem.mime && !this.draggedItem.url) ||
        (this.draggedItem.children && Object.keys(this.draggedItem.children).length);

      const body = {
        id: this.draggedItem.id,
        relative_path: newPath,
        is_directory: isFolder,
      };

      this.updateFile(body);

      this.internalDragActive = false;
      this.draggedItem = null;
      this.dragOverId = null;
    },

    openContextMenu(event, file) {
      event.preventDefault();
      event.stopPropagation();

      this.closePreview();

      const rect = this.$el.getBoundingClientRect();
      const clickX = event.clientX - rect.left;
      const clickY = event.clientY - rect.top;

      const menuW = 140;
      const menuH = 44;
      const maxX = rect.width - menuW - 4;
      const maxY = rect.height - menuH - 4;

      this.contextMenu = {
        visible: true,
        x: Math.max(4, Math.min(clickX, maxX)),
        y: Math.max(4, Math.min(clickY, maxY)),
        file,
      };
    },

    handleRootContext(event) {
      this.closePreview();

      const rect = this.$el.getBoundingClientRect();
      const clickX = event.clientX - rect.left;
      const clickY = event.clientY - rect.top;

      const menuW = 160;
      const menuH = 120;
      const maxX = rect.width - menuW - 4;
      const maxY = rect.height - menuH - 4;

      const targetFileItem = event.target.closest(".file-item, .repo-row");

      if (targetFileItem) return;

      this.contextMenu = {
        visible: true,
        x: Math.max(4, Math.min(clickX, maxX)),
        y: Math.max(4, Math.min(clickY, maxY)),
        file: null,
      };
    },

    handlePreviewEnter(event, file) {
      const kind = this.getPreviewKind(file);

      if (!kind) return;
      if (this.internalDragActive) return;

      this.clearPreviewTimer();
      this.clearPreviewCloseTimer();

      // O rect precisa ser lido agora: currentTarget é anulado após o dispatch do evento.
      const position = this.getPreviewPosition(event.currentTarget.getBoundingClientRect());

      this.previewTimer = setTimeout(() => {
        if (this.preview.visible && this.preview.file && this.preview.file.id === file.id) return;

        this.hasPreviewLoad = true;
        this.previewText = "";

        this.preview = { visible: true, x: position.x, y: position.y, file };

        if (kind === "text") this.fetchPreviewText(file);
      }, PREVIEW_DELAY);
    },

    handlePreviewLeave() {
      this.clearPreviewTimer();

      if (!this.preview.visible) return;

      this.schedulePreviewClose();
    },

    handlePreviewBoxLeave(event) {
      const rect = event.currentTarget.getBoundingClientRect();

      const isPointerInsideBox =
        event.clientX >= rect.left &&
        event.clientX <= rect.right &&
        event.clientY >= rect.top &&
        event.clientY <= rect.bottom;

      // Alguns navegadores disparam mouseleave ao cruzar para dentro do iframe do
      // PDF; nesse caso o ponteiro segue dentro do preview e ele não deve fechar.
      if (isPointerInsideBox) return;

      this.schedulePreviewClose();
    },

    handleListClick(file) {
      if (this.searchMode) {
        this.handleSearchClick(file);

        return;
      }

      this.handleFileSelect(file);
    },

    handlePathReturn() {
      const current = this.$store.getters["admin/getRepoSelectedPath"];

      if (current) {
        const pathParts = current.split("/").filter((part) => part);

        pathParts.pop();

        const newPath = pathParts.length ? `/${pathParts.join("/")}/` : null;

        this.$store.dispatch("admin/setRepoSelectedPath", newPath);
      }
    },

    handleSearchClick(file) {
      const isFolder = !file.mime && !file.url;

      if (this.selectMode && !isFolder) {
        this.$emit("file-selected", file);

        return;
      }

      let targetPath = file.relative_path ? file.relative_path : "/";

      if (!isFolder) {
        const pathParts = targetPath.split("/").filter((part) => part);

        pathParts.pop();

        targetPath = pathParts.length ? `/${pathParts.join("/")}/` : "/";
      }

      this.$store.dispatch("admin/setRepoSelectedPath", targetPath);

      this.clearSearch();
    },

    handleFileSelect(file) {
      const isFolder = (!file.mime && !file.url) || (file.children && Object.keys(file.children).length);

      if (isFolder && file.relative_path && typeof file.relative_path === "string") {
        this.$store.dispatch("admin/setRepoSelectedPath", file.relative_path);

        return;
      }

      if (this.selectMode) {
        this.$emit("file-selected", file);

        return;
      }

      window.open(file.url, "_blank");
    },

    async handleFileRename(file) {
      const isFolder = (!file.mime && !file.url) || (file.children && Object.keys(file.children).length);

      this.editingName = isFolder ? file.name : this.getNameWithoutExtension(file.name);
      this.nameEditingId = file.name;

      await this.$nextTick();

      const inputElement = this.$el.querySelector("#file-name-input");

      if (inputElement) inputElement.focus();
    },

    handleFileDownload(file) {
      fetch(file.url)
        .then((response) => response.blob())
        .then((blob) => {
          const link = document.createElement("a");
          const url = window.URL.createObjectURL(blob);

          link.href = url;
          link.download = file.name;
          document.body.appendChild(link);

          link.click();

          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        })
        .catch(() => {
          this.$bvToast.toast("Erro ao baixar o arquivo", {
            title: "Erro",
            variant: "danger",
            solid: true,
          });
        });
    },

    handleCreateFolder() {
      const folderInput = prompt("Digite o nome da nova pasta:");

      if (folderInput) {
        const relativePath = this.currentPath === "/" ? "" : this.currentPath;

        const folderName = this.getFolderName(folderInput);

        const data = {
          workflow_id: this.$service.getWorkflowId(),
          name: folderName,
          base64File: null,
          relative_path: `${relativePath}/${folderName}`,
          is_directory: true,
        };

        this.$store
          .dispatch("admin/createRepoFile", data)
          .then(() => {
            this.$bvToast.toast(`Pasta ${folderName} criada com sucesso!`, {
              title: "Sucesso",
              variant: "success",
              solid: true,
            });

            this.$emit("local-refresh");
          })
          .finally(() => {
            this.hasSaveRequest = false;
          });
      }
    },

    handleUploadFiles() {
      const fileInput = document.createElement("input");

      fileInput.type = "file";
      fileInput.multiple = true;
      fileInput.accept = MEDIA_ACCEPT_ATTRIBUTE;

      fileInput.click();

      fileInput.onchange = async (event) => {
        const files = event.target.files;

        if (files.length > 0) {
          await this.uploadFilesSequentially(files);
        }
      };
    },

    handleConfirmNameEdit(file) {
      const isFolder = (!file.mime && !file.url) || (file.children && Object.keys(file.children).length);

      const sameFileNameOnFolder = isFolder
        ? this.currentFiles[this.editingName]
        : this.currentFiles[this.editingName + "." + file.name.split(".").pop()];

      if (sameFileNameOnFolder) {
        if (sameFileNameOnFolder.id === file.id) {
          this.clearNameEdit();

          return;
        }

        this.$bvToast.toast(`Já existe um arquivo com o nome ${this.editingName} neste diretório.`, {
          title: "Erro ao renomear",
          variant: "warning",
          solid: true,
        });

        return;
      }

      const newName = isFolder ? this.editingName : this.editingName + "." + file.name.split(".").pop();

      const body = {
        id: file.id,
        name: newName,
        is_directory: isFolder,
      };

      this.updateFile(body);

      this.clearNameEdit();
    },

    async uploadFilesSequentially(files) {
      const { validFiles, errors } = partitionMediaFiles(files);

      if (errors.length) {
        this.$bvToast.toast(errors.join(" "), {
          title: "Arquivo não suportado",
          variant: "danger",
          solid: true,
        });
      }

      if (!validFiles.length) return;

      let successCount = 0;
      let errorCount = 0;

      this.hasSaveRequest = true;

      for (let i = 0; i < validFiles.length; i++) {
        const file = validFiles[i];

        try {
          const base64 = await this.readFileAsBase64(file);

          const crudeFile = {
            name: this.getFileName(file.name),
            base64File: base64,
          };

          await this.createFileSequentially(crudeFile);

          successCount++;
        } catch (error) {
          errorCount++;
        }
      }

      if (successCount > 0) {
        this.$bvToast.toast(
          `${successCount} arquivo${successCount > 1 ? "s" : ""} enviado${
            successCount > 1 ? "s" : ""
          } com sucesso!`,
          {
            title: "Sucesso",
            variant: "success",
            solid: true,
          },
        );
      }

      if (errorCount > 0) {
        this.$bvToast.toast(`${errorCount} arquivo${errorCount > 1 ? "s falharam" : " falhou"}`, {
          title: "Erro",
          variant: "danger",
          solid: true,
        });
      }

      if (successCount > 0) this.$emit("local-refresh");

      this.hasSaveRequest = false;
    },

    readFileAsBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = (e) => {
          resolve(e.target.result);
        };

        reader.onerror = (error) => {
          reject(error);
        };

        reader.readAsDataURL(file);
      });
    },

    createFileSequentially(file) {
      const nameWithoutType = this.getNameWithoutExtension(file.name);

      const relativePath = this.currentPath === "/" ? "" : this.currentPath;

      const data = {
        workflow_id: this.$service.getWorkflowId(),
        name: nameWithoutType,
        base64File: file.base64File,
        relative_path: `${relativePath}/${file.name}`,
        is_directory: false,
      };

      return this.$store.dispatch("admin/createRepoFile", data);
    },

    createFile(file) {
      if (this.hasSaveRequest) return;

      this.hasSaveRequest = true;

      const nameWithoutType = this.getNameWithoutExtension(file.name);

      const relativePath = this.currentPath === "/" ? "" : this.currentPath;

      const data = {
        workflow_id: this.$service.getWorkflowId(),
        name: nameWithoutType,
        base64File: file.base64File,
        relative_path: `${relativePath}/${file.name}`,
        is_directory: false,
      };

      this.$store
        .dispatch("admin/createRepoFile", data)
        .then(() => {
          this.$bvToast.toast(`Arquivo ${file.name} enviado com sucesso!`, {
            title: "Sucesso",
            variant: "success",
            solid: true,
          });

          this.$emit("local-refresh");
        })
        .finally(() => {
          this.hasSaveRequest = false;
        });
    },

    updateFile(body) {
      if (this.hasSaveRequest) return;

      this.hasSaveRequest = true;

      this.$store
        .dispatch("admin/updateRepoFile", body)
        .then(() => {
          this.$bvToast.toast(`Arquivo atualizado com sucesso!`, {
            title: "Sucesso",
            variant: "success",
            solid: true,
          });

          this.$emit("local-refresh");
        })
        .finally(() => {
          this.hasSaveRequest = false;
        });
    },

    async fetchPreviewText(file) {
      const requestId = this.previewRequestId;

      try {
        const response = await fetch(file.url, { mode: "cors" });

        const reader = response.body.getReader();
        const { value } = await reader.read();

        reader.cancel();

        if (requestId !== this.previewRequestId) return;

        this.previewText = new TextDecoder().decode(value).slice(0, PREVIEW_TEXT_LIMIT);
        this.hasPreviewLoad = false;
      } catch (error) {
        if (requestId !== this.previewRequestId) return;

        this.closePreview();
      }
    },

    deleteFile(file_id) {
      if (this.hasSaveRequest) return;

      this.hasSaveRequest = true;

      this.$store
        .dispatch("admin/deleteRepoFile", file_id)
        .then(() => {
          this.$bvToast.toast(`Arquivo deletado com sucesso!`, {
            title: "Sucesso",
            variant: "success",
            solid: true,
          });

          this.$emit("local-refresh");
        })
        .finally(() => {
          this.hasSaveRequest = false;
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.repo-container {
  display: flex;
  flex-direction: column;
  position: relative;
  height: 100%;
  width: 100%;
  gap: 10px;
  overflow: hidden;

  &.dragging {
    .drag-overlay {
      display: flex;
      opacity: 1;
    }
  }

  .drag-overlay {
    display: flex;
    justify-content: center;
    align-items: center;
    pointer-events: none;
    position: absolute;
    opacity: 0;
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;
    z-index: 1000;
    border-radius: 8px;
    border: 2px dashed #046af3;
    transition: opacity 0.2s ease;
    background: rgba(255, 255, 255, 0.95);

    .drag-content {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 16px;

      p {
        font-size: 16px;
        font-weight: 500;
        color: #14223b;
        margin: 0;
      }
    }
  }

  .filter-holder {
    display: flex;
    justify-content: space-between;
    align-items: center;
    min-height: 45px;
    width: 100%;

    .line-holder {
      display: flex;
      align-items: center;
      gap: 12px;

      .back-icon {
        font-size: 24px;
        min-width: 24px;
        min-height: 24px;
        color: #2f343d;
        cursor: pointer;
      }

      .path {
        font-size: 21px;
        color: #2f343d;
      }
    }

    .actions-holder {
      display: flex;
      align-items: center;
      gap: 12px;

      .search-input {
        max-width: 400px;
      }

      .view-mode-toggle {
        display: flex;
        align-items: center;
        flex-shrink: 0;
        border-radius: 8px;
        border: 1px solid #f5f6f8;
        background: #ffffff;
        overflow: hidden;

        .toggle-option {
          display: flex;
          justify-content: center;
          align-items: center;
          flex-shrink: 0;
          height: 40px;
          width: 40px;
          min-height: 40px;
          min-width: 40px;
          color: #7d8797;
          cursor: pointer;

          &:hover {
            background: #f5f7fb;
          }

          &.active {
            background: #eaf2fe;
            color: #046af3;
          }

          .toggle-icon {
            font-size: 22px;
            min-height: 22px;
            min-width: 22px;
          }
        }
      }
    }
  }

  .empty-repo {
    font-size: 14px;
    color: #8e8e8e;
  }

  .files-box {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    grid-auto-rows: 150px;
    justify-content: start;
    align-content: start;
    align-items: start;
    height: 100%;
    width: 100%;
    padding: 1rem;
    gap: 12px;
    border-radius: 8px;
    background: #ffffff;
    overflow-y: auto;
    overflow-x: hidden;
    user-select: none;

    .empty-repo {
      grid-column: 1 / -1;
    }

    .file-item {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      position: relative;
      height: 150px;
      width: 150px;
      padding: 1rem;
      gap: 5px;
      border-radius: 8px;
      box-shadow: 0px 0px 4px 1px #00000012;
      overflow: hidden;
      cursor: pointer;

      &.drop-target {
        outline: 2px dashed #046af3;
        outline-offset: -2px;
        background: #eaf2fe;
      }

      .file-counter {
        display: flex;
        justify-content: center;
        align-items: center;
        position: absolute;
        font-size: 12px;
        top: 8px;
        right: 8px;
        width: 24px;
        height: 24px;
        border-radius: 100%;
        background: #2f343d;
        color: #ffffff;
        overflow: hidden;

        span {
          text-overflow: ellipsis;
          white-space: nowrap;
          max-width: 100%;
          overflow: hidden;
        }
      }

      .file-icon {
        font-size: 96px;
        min-width: 96px;
        min-height: 96px;
      }

      .file-name {
        text-overflow: ellipsis;
        white-space: nowrap;
        min-height: 24px;
        line-height: 24px;
        max-width: 100%;
        overflow: hidden;
      }

      .file-name-input-holder {
        display: flex;
        align-items: center;
        min-height: 24px;
        width: 100%;
        gap: 5px;
        overflow: visible;

        input {
          height: 100%;
          width: calc(100% - 16px);
          padding: 4px 8px;
          border-radius: 4px;
          border: 1px solid #f5f6f8;
          outline: none;
        }

        .name-editing-icon {
          font-size: 16px;
          min-height: 16px;
          min-width: 16px;
          color: #4caf50;
          cursor: pointer;
        }
      }
    }
  }

  .repo-context-menu {
    position: absolute;
    user-select: none;
    font-size: 16px;
    min-width: 200px;
    z-index: 500;
    border-radius: 10px;
    border-top: 1px solid #f5f6f8;
    box-shadow: 0px 1px 2px 1px #00000022;
    background: #ffffff;
    overflow: hidden;

    ul {
      list-style: none;
      padding: 0;
      margin: 0;
    }

    li {
      text-align: center;
      line-height: 18px;
      font-weight: 300;
      padding: 10px 20px;
      border-bottom: 1px solid #f5f6f8;
      color: #6e6b7b;
      cursor: pointer;

      &:last-child {
        border-bottom: none;
      }

      &:hover {
        background: #f5f7fb;
      }
    }
  }

  .repo-list {
    display: flex;
    flex-direction: column;
    height: 100%;
    width: 100%;
    padding: 1rem;
    gap: 4px;
    border-radius: 8px;
    background: #ffffff;
    overflow-y: auto;
    overflow-x: hidden;
    user-select: none;

    .list-header {
      display: flex;
      align-items: center;
      position: sticky;
      font-size: 12px;
      font-weight: 500;
      top: 0;
      gap: 10px;
      padding: 0 10px 8px 10px;
      border-bottom: 1px solid #f5f6f8;
      background: #ffffff;
      color: #7a7f89;

      .header-name {
        flex: 1;
        min-width: 0;
      }

      .header-path {
        flex-shrink: 0;
        width: 220px;
      }

      .header-date {
        text-align: right;
        flex-shrink: 0;
        width: 100px;
      }
    }

    .repo-row {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 10px;
      border-radius: 8px;
      cursor: pointer;

      &:hover {
        background: #f7f9fc;
      }

      &.drop-target {
        outline: 2px dashed #046af3;
        outline-offset: -2px;
        background: #eaf2fe;
      }

      .row-icon {
        font-size: 24px;
        min-width: 24px;
        min-height: 24px;
      }

      .row-name {
        display: flex;
        align-items: center;
        font-size: 14px;
        flex: 1;
        min-width: 0;
        gap: 6px;
        color: #2f343d;

        .row-name-text {
          text-overflow: ellipsis;
          white-space: nowrap;
          overflow: hidden;
        }

        .row-count {
          flex-shrink: 0;
          color: #7a7f89;
        }
      }

      .row-name-input-holder {
        display: flex;
        align-items: center;
        flex: 1;
        min-width: 0;
        gap: 5px;

        input {
          width: 100%;
          padding: 4px 8px;
          border-radius: 4px;
          border: 1px solid #f5f6f8;
          outline: none;
        }

        .name-editing-icon {
          font-size: 16px;
          min-height: 16px;
          min-width: 16px;
          color: #4caf50;
          cursor: pointer;
        }
      }

      .row-path {
        text-overflow: ellipsis;
        white-space: nowrap;
        font-size: 12px;
        flex-shrink: 0;
        width: 220px;
        color: #7a7f89;
        overflow: hidden;
      }

      .row-date {
        text-align: right;
        white-space: nowrap;
        font-size: 12px;
        flex-shrink: 0;
        width: 100px;
        color: #7a7f89;
      }
    }
  }

  .repo-preview {
    position: absolute;
    width: 280px;
    padding: 8px;
    z-index: 1200;
    border-radius: 8px;
    box-shadow: 0px 1px 6px 2px #00000022;
    background: #ffffff;

    .preview-content-holder {
      display: flex;
      justify-content: center;
      align-items: center;
      height: 240px;
      width: 100%;
      border-radius: 4px;
      background: #f7f9fc;
      overflow: hidden;

      .preview-media {
        pointer-events: none;
        max-height: 100%;
        max-width: 100%;
        object-fit: contain;
      }

      .preview-frame {
        height: 100%;
        width: 100%;
        border: none;
      }

      .preview-text {
        pointer-events: none;
        word-break: break-word;
        white-space: pre-wrap;
        font-size: 10px;
        line-height: 1.4;
        height: 100%;
        width: 100%;
        padding: 6px;
        margin: 0;
        color: #2f343d;
        overflow: hidden;
      }
    }

    .preview-name {
      text-overflow: ellipsis;
      text-align: center;
      white-space: nowrap;
      font-size: 12px;
      padding-top: 6px;
      color: #2f343d;
      overflow: hidden;
    }
  }

  .absolute-loading {
    display: flex;
    justify-content: center;
    align-items: center;
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;
    z-index: 1500;
    background: rgba(255, 255, 255, 0.7);
  }
}

@media (max-width: 800px) {
  .repo-container {
    .repo-list {
      .list-header {
        .header-path,
        .header-date {
          display: none;
        }
      }

      .repo-row {
        .row-path,
        .row-date {
          display: none;
        }
      }
    }
  }
}
</style>
